import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';

import Autocomplete from '@shared/atoms/Autocomplete';
import T from '@shared/atoms/Typography';
import PlaylistAdministration from '@shared/sections/PlaylistAdministration';
import { PlaylistProvider } from '@shared/sections/PlaylistAdministration/context';
import theme from '@shared/themes/default';

import { useAppGlobals } from '../../contexts/AppContext';
import { clientUrl, getStaticImagesHostname } from '../../../config';
import { listOrganizations } from './graphql/queries';
import { listOrganizationOptions } from '../../../utils';

const AutocompleteWrapper = styled.div`
  max-width: 400px;
  margin-bottom: 1.5rem;
`;

const PlaylistView = () => {
  const {
    domain,
    getRestClient,
    locale,
    useGraphQLMutation,
    useLazyGraphQLQuery,
    useNewGraphQLQuery,
    videocationCatalog,
  } = useAppGlobals();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { t } = useTranslation();

  const [organization, setOrganization] = useState(null);
  const [organizationList, setOrganizationList] = useState([]);

  const searchParams = new URLSearchParams(search);
  const preSelectedOrganizationId = searchParams.get('organization');
  const preSelectedManagerId = searchParams.get('manager');
  const preSelectedPlaylistId = searchParams.get('playlist');
  const preSelectedType = searchParams.get('type');

  const previewBaseUrl = clientUrl.replace('{DOMAIN}', domain);

  const listOrganizationsResponse = useNewGraphQLQuery(listOrganizations, {
    variables: {
      pagination: { limit: 1000 },
    },
  });

  useEffect(() => {
    const { loading, data } = listOrganizationsResponse;
    if (!loading && data?.listOrganizations) {
      setOrganizationList(data.listOrganizations);
    }
  }, [listOrganizationsResponse]);

  const seeActivityHandler = (id) => {
    navigate(
      `/activity?organization=${organization.id}&view=playlists&id=${id}`
    );
  };

  const selectOrganizationHandler = (organization) => {
    if (organization) {
      const { value: organizationId } = organization;
      navigate(`/playlists?organization=${organizationId}`);
    } else {
      navigate('/playlists');
    }
  };

  useEffect(() => {
    if (organizationList.length > 0) {
      setOrganization();

      if (
        !preSelectedOrganizationId ||
        preSelectedOrganizationId === 'global'
      ) {
        setOrganization({ id: 'global' });
      } else {
        setOrganization(
          organizationList.find(
            (organization) => organization.id === preSelectedOrganizationId
          )
        );

        // TODO: Error handling for the case the organization is not found
      }
    }
  }, [preSelectedOrganizationId, organizationList]);

  return (
    <div>
      <T
        color={theme.palette.lightPure}
        sx={{
          marginBottom: theme.spacing.m1,
        }}
        variant="subtitle"
      >
        {t('backoffice.general.findOrganization')}
      </T>
      <AutocompleteWrapper>
        <Autocomplete
          filterOptions
          getOptionKey={(option) => option.value}
          getOptionLabel={(option) => option.name}
          loading={listOrganizationsResponse.loading}
          onChange={selectOrganizationHandler}
          options={listOrganizationOptions(organizationList)}
          preSelectedValue={
            organization && organization.id !== 'global'
              ? {
                  name: organization.name
                    ? `${organization.archived ? '(Archived) ' : ''}${organization.name}`
                    : `No name (ID: ${organization.id})`,
                  value: organization.id,
                }
              : null
          }
        />
      </AutocompleteWrapper>
      {organization && (
        <PlaylistProvider
          domain={domain}
          locale={locale}
          organization={organization}
          managerId={preSelectedManagerId || 'all'}
          type={preSelectedType}
          handleHistory={(search) =>
            navigate(
              {
                pathname,
                search: [`organization=${organization.id}`, search].join('&'),
              },
              { replace: true }
            )
          }
          playlistId={preSelectedPlaylistId}
          seeActivityHandler={seeActivityHandler}
          restClient={getRestClient()}
          useGraphQLMutation={useGraphQLMutation}
          useLazyGraphQLQuery={useLazyGraphQLQuery}
          hasSubscription
          hasVideocationCatalogAccess={
            !!organization?.subscription?.product.catalogs.find(
              (catalog) => catalog.id === videocationCatalog?.id
            )
          }
          isSubscriptionAdmin
          staticImagesHostname={getStaticImagesHostname()}
          previewBaseUrl={previewBaseUrl}
          key={organization.id} // TODO: This is just a quick fix for the fact queries don't retrigger when the organization changes
        >
          <PlaylistAdministration isBackOffice key={organization.id} />
        </PlaylistProvider>
      )}
    </div>
  );
};

export default PlaylistView;
